import React, { useState, useEffect } from 'react';
import { 
  subscribeToPushNotifications, 
  unsubscribeFromPushNotifications,
  isPushNotificationEnabled,
  reconnectPushSubscription
} from '../utils/pushNotifications';

// iOS Install Modal Component
const IOSInstallModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}
    onClick={(e) => {
      // Close modal when clicking the backdrop (outside the modal content)
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}
    >
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '95%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '24px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        animation: 'modalFadeIn 0.3s ease-out',
        position: 'relative'
      }}>
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#9ca3af'
          }}
        >
          ×
        </button>
        
        <h2 style={{ 
          margin: '0 0 16px',
          color: '#1f2937',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          Enable Notifications
        </h2>
        
        <div style={{ marginBottom: '24px' }}>
          <p style={{ 
            margin: '0 0 16px',
            color: '#4b5563',
            lineHeight: '1.5'
          }}>
            For notifications to work on iOS, you need to add this app to your home screen.
          </p>
          
          <ol style={{ 
            margin: '0 0 16px',
            paddingLeft: '24px',
            color: '#4b5563',
            lineHeight: '1.7'
          }}>
            <li>Click the <strong>Share</strong> button <span style={{ fontSize: '18px' }}>⬆️</span> at the bottom of the screen</li>
            <li>Then click <strong>Add to Home Screen</strong></li>
            <li>Tap <strong>Add</strong> in the top right corner</li>
            <li>Launch the app from your home screen</li>
          </ol>
        </div>
        
        <div style={{ 
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '120px'
            }}
          >
            Got it
          </button>
        </div>
        
        <style>{`
          @keyframes modalFadeIn {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </div>
  );
};

const NotificationSettings = () => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Detect platforms
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isMacOS = /Macintosh/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // Function to render the toggle button
  const renderToggleButton = () => {
    // Don't render until status check is complete
    if (!statusChecked && !error) {
      return (
        <button
          disabled
          style={{
            padding: '10px 18px',
            backgroundColor: '#e2e8f0',
            color: '#4a5568',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'wait',
            width: '150px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <span 
            className="loading-spinner"
            style={{
              width: '16px',
              height: '16px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
          <span>Checking...</span>
        </button>
      );
    }

    return (
      <div style={{ position: 'relative' }}>
        {error && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            marginTop: '8px',
            whiteSpace: 'nowrap',
            zIndex: 10,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            maxWidth: '250px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        <button
          onClick={handleToggleNotifications}
          disabled={loading}
          style={{
            padding: '10px 18px',
            backgroundColor: notificationsEnabled ? '#4338ca' : '#f3f4f6',
            color: notificationsEnabled ? 'white' : '#3b82f6',
            border: notificationsEnabled ? 'none' : '1px solid #d1d5db',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: loading ? 'wait' : 'pointer',
            transition: 'all 0.2s ease',
            width: '150px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {loading ? (
            <>
              <span 
                className="loading-spinner"
                style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: `2px solid ${notificationsEnabled ? 'white' : '#3b82f6'}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
              <span>Processing...</span>
            </>
          ) : (
            notificationsEnabled ? 'Notifications On' : 'Notifications Off'
          )}
        </button>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  };

  useEffect(() => {
    // Only check status once on load
    const checkStatus = async () => {
      try {
        console.log('Checking notification status...');
        setLoading(true);
        
        // Check if we have a cached result - this will be used by isPushNotificationEnabled()
        if (typeof window._pushNotificationStatusCache !== 'undefined') {
          console.log('Using cached notification status:', window._pushNotificationStatusCache);
          setNotificationsEnabled(window._pushNotificationStatusCache);
          setStatusChecked(true);
          setError(null);
          setLoading(false);
          
          // With cached status, don't attempt automatic reconnection
          // Only reconnect when explicitly toggling the notification switch
          console.log('Using cached status without automatic reconnection');
          return;
        }
        
        const enabled = await isPushNotificationEnabled();
        console.log('Notification status:', enabled);
        
        // Only attempt reconnection if we're not already in a reconnection process
        if (enabled && Notification.permission === 'granted' && !window._reconnectionInProgress) {
          console.log('Attempting to reconnect subscription if needed...');
          // Add a short timeout to avoid any synchronization issues
          setTimeout(async () => {
            await reconnectPushSubscription();
          }, 500);
        }
        
        setNotificationsEnabled(enabled);
        setStatusChecked(true);
        // Reset error if status check succeeds
        setError(null);
      } catch (err) {
        console.error('Error checking notification status:', err);
        setError('Could not check notification status');
      } finally {
        setLoading(false);
      }
    };
    
    if (!statusChecked) {
      checkStatus();
    }
  }, [statusChecked]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleToggleNotifications = async () => {
    if (loading) return;

    setLoading(true);
    setError(null);
    
    // Increment attempt count to detect rapid toggling
    setAttemptCount(prev => prev + 1);
    
    // Store the current state to prevent rapid toggling
    const wasEnabled = notificationsEnabled;

    try {
      console.log('Current notification state:', wasEnabled);
      
      if (wasEnabled) {
        console.log('Attempting to unsubscribe...');
        const success = await unsubscribeFromPushNotifications();
        console.log('Unsubscribe result:', success);
        
        if (success) {
          setNotificationsEnabled(false);
          console.log('Successfully unsubscribed');
        } else {
          throw new Error('Failed to unsubscribe from notifications');
        }
      } else {
        // First, check current permission status
        console.log('Current permission status:', Notification.permission);
        
        if (Notification.permission === 'default') {
          console.log('Requesting notification permission...');
          const permission = await Notification.requestPermission();
          console.log('Permission response:', permission);
          
          if (permission !== 'granted') {
            throw new Error('Notification permission not granted');
          }
        } else if (Notification.permission !== 'granted') {
          throw new Error('Notification permission denied');
        }

        console.log('Attempting to subscribe...');
        const success = await subscribeToPushNotifications();
        
        if (success) {
          console.log('Successfully subscribed');
          setNotificationsEnabled(true);
        } else {
          throw new Error('Failed to subscribe to notifications');
        }
      }
    } catch (err) {
      console.error('Error toggling notifications:', err);
      
      // Detect rapid toggling (more than 3 attempts in quick succession)
      if (attemptCount > 2) {
        setError('Too many attempts. Please reload the page and try again.');
        // Reset attempts after 10 seconds
        setTimeout(() => setAttemptCount(0), 10000);
        return;
      }
      
      // Set a more user-friendly error message
      let errorMessage = err.message || 'Failed to toggle notifications';
      
      if (errorMessage.includes('permission')) {
        errorMessage = 'Browser permission denied. Check your browser settings.';
      } else if (errorMessage.includes('Failed to subscribe')) {
        errorMessage = 'Could not subscribe to notifications. Try reloading the page.';
      } else if (errorMessage.includes('Failed to unsubscribe')) {
        errorMessage = 'Could not turn off notifications. Try reloading the page.';
      } else if (errorMessage.includes('User ID not found')) {
        errorMessage = 'Session expired. Please log out and log back in.';
      }
      
      setError(errorMessage);
      
      // Don't change UI state on error to prevent flickering
      // The actual state will be determined later
    } finally {
      // After completing the operation, verify the actual state
      try {
        console.log('Verifying final notification status...');
        const actualEnabled = await isPushNotificationEnabled();
        console.log('Final notification status:', actualEnabled);
        
        // Update the status cache
        window._pushNotificationStatusCache = actualEnabled;
        
        // Only update if different from current UI state to prevent flickering
        if (actualEnabled !== notificationsEnabled) {
          setNotificationsEnabled(actualEnabled);
        }
      } catch (statusErr) {
        console.error('Error verifying final status:', statusErr);
        // Keep previous state on verification error
      }
      
      setLoading(false);
    }
  };

  // Don't show the button if notifications aren't supported
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return (
      <button
        disabled
        style={{
          padding: '8px 16px',
          backgroundColor: '#e2e8f0',
          color: '#718096',
          border: 'none',
          borderRadius: '8px',
          cursor: 'not-allowed',
          opacity: 0.7,
          width: '150px'
        }}
      >
        Not Supported
      </button>
    );
  }

  // If permission is already denied, show a different button
  if (Notification.permission === 'denied') {
    return (
      <button
        onClick={() => {
          alert('Please enable notifications in your browser settings and refresh this page.');
          // If using Chrome
          if (navigator.userAgent.includes('Chrome')) {
            window.open('chrome://settings/content/notifications', '_blank');
          }
        }}
        style={{
          padding: '8px 16px',
          backgroundColor: '#fee2e2',
          color: '#b91c1c',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          width: '150px'
        }}
      >
        Enable in Browser
      </button>
    );
  }
  
  // For iOS users who haven't added to home screen
  if (isIOS && !isStandalone) {
    // Show modal on page load
    useEffect(() => {
      // Only show once per session
      setShowIOSModal(true);
    }, []);
    
    return (
      <>
        <button
          onClick={() => setShowIOSModal(true)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            width: '150px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '18px' }}>📱</span>
          Add to Home Screen
        </button>
        
        <IOSInstallModal 
          isOpen={showIOSModal} 
          onClose={() => setShowIOSModal(false)} 
        />
      </>
    );
  }
  
  // For macOS Safari users, check version and provide instructions
  if (isMacOS && isSafari) {
    const safariVersionMatch = navigator.userAgent.match(/Version\/(\d+)\.(\d+)(?:\.(\d+))?.*Safari/);
    if (safariVersionMatch) {
      const major = parseInt(safariVersionMatch[1], 10);
      const minor = parseInt(safariVersionMatch[2], 10);
      
      // If Safari version is below 16.4, show version warning
      if (major < 16 || (major === 16 && minor < 4)) {
        return (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            color: '#856404', 
            borderRadius: '8px',
            marginBottom: '16px' 
          }}>
            <p><strong>Safari Version Notice:</strong> Push notifications on macOS require Safari 16.4 or newer. Your current version is {major}.{minor}.</p>
            <p>Please update Safari to receive notifications.</p>
          </div>
        );
      }
      
      // For Safari 16.4+, provide system settings guidance
      return (
        <div>
          {renderToggleButton()}
        </div>
      );
    }
  }

  // Default return (for non-iOS, non-macOS platforms)
  return renderToggleButton();
};

export default NotificationSettings;